import { useTheme } from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { Row, Col, Collapse } from "react-bootstrap";
import CurrencyInput from "react-currency-input-field";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import { toast } from "Layout/slice";
import { Button } from "components/Button";
import { IconArrowDown } from "assets/icons";
import { Placeholder } from "components/Placeholder";
import { Title } from "style/components/Title.styles";
import { reconnectPortfolio } from "features/Dashboard/service";
import { PortfolioBoxContainer } from "./ManageInvestments.styles";
import StopStrategyModal from "shared/StrategyControl/StopStrategyModal";
import { getBrokerIcon, openNewTab, parseErrorMessage, roundOff } from "utils";

export const PortfolioBox = ({
  strategy,
  broker,
  handleStoppedStrategy,
  handleAllocationChange,
  errorMessage,
}) => {
  const theme = useTheme();

  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(false);
  const [allocationValue, setAllocationValue] = useState(0);
  const [showStopStrategyModal, setStopStrategyModal] = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(false);

  const {
    account_nickname,
    account_value,
    total_cash,
    icon,
    trading_connection,
    broker_account_id,
    is_stale,
    existing_allocation,
  } = broker;

  const oldAllocationValue =
    trading_connection.current_allocation.dollars.raw_number;

  useEffect(() => {
    setAllocationValue(oldAllocationValue);
  }, [trading_connection]);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const handleAllocationAmountChange = (value) => {
    setAllocationValue(value);
    handleAllocationChange(value);
  };

  const toggleStopStrategyModal = () => {
    setStopStrategyModal(!showStopStrategyModal);
  };

  const handleReconnectClick = async () => {
    try {
      setIsCardLoading(true);
      const response = await reconnectPortfolio(broker_account_id);
      openNewTab(response.redirect.redirectURI);
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsCardLoading(false);
    }
  };

  const handleStopClick = () => {
    handleStoppedStrategy();
    toggleStopStrategyModal();
  };

  const percent = useMemo(() => {
    const percentage =
      (allocationValue / broker.account_value.raw_number) * 100;
    const parsePercentage = roundOff(percentage, 2);

    return percentage > 100 ? "--" : `${parsePercentage}%`;
  }, [allocationValue]);

  return (
    <>
      <PortfolioBoxContainer
        className={classNames({
          collapsed: collapsed,
        })}
      >
        <div className="header">
          <div className="portfolio-wrapper">
            <img src={getBrokerIcon(icon, theme.mode)} className="broker-img" />
            <div
              className="broker-name"
              onClick={toggleCollapsed}
              aria-controls="collapse-details"
              aria-expanded={collapsed}
            >
              {account_nickname}{" "}
              <IconArrowDown
                fill={theme.tab.color.secondary}
                className={classNames({ "collapsed-icon": collapsed })}
              />
            </div>
          </div>
          {collapsed && !is_stale && (
            <Button className="btn-stop" onClick={toggleStopStrategyModal}>
              Stop strategy
            </Button>
          )}
        </div>

        <Collapse in={collapsed && !is_stale}>
          <div id="collapse-details ">
            <Row className="details">
              <Col md={3} xs={6} className="spacing pr-0">
                <div className="label">Porfolio amount</div>
                <div className="value"> {account_value?.formatted}</div>
              </Col>
              <Col md={3} xs={6} className="spacing p-0">
                <div className="label">Allocated amount</div>
                <div className="value"> {existing_allocation?.formatted}</div>
              </Col>
              <Col md={3} xs={6} className="spacing">
                <div className="label"> Buying power</div>
                <div className="value"> {total_cash?.formatted}</div>
              </Col>
              <Col md={3} className="spacing md-mt-0">
                <div className="label"> Allocation value</div>
                <div className="amount">
                  <div>
                    <div className="allocation-input-wrapper">
                      <div className="negative-margin">$</div>
                      <CurrencyInput
                        id="allocation-price"
                        name="allocation-price"
                        decimalsLimit={2}
                        decimalScale={2}
                        className="allocation-price-input"
                        allowDecimals={true}
                        defaultValue={allocationValue}
                        value={allocationValue}
                        onValueChange={handleAllocationAmountChange}
                      />
                      <div className="percentage">{percent}</div>
                    </div>
                  </div>
                </div>
                <div className="error-message">{errorMessage}</div>

                <div className="min-value">Min allocation value: $100</div>
              </Col>
            </Row>
          </div>
        </Collapse>

        <Collapse in={collapsed && !isCardLoading && is_stale}>
          <div id="collapse-details">
            <div className="stale-wrapper">
              <div className="label">STALE</div>
              <Button onClick={handleReconnectClick}>Reconnect</Button>
            </div>
          </div>
        </Collapse>

        {isCardLoading && (
          <div className="stale-wrapper">
            <Placeholder size="lg" as={Title} isLoading={isCardLoading} />
          </div>
        )}
      </PortfolioBoxContainer>

      {showStopStrategyModal && (
        <StopStrategyModal
          show={showStopStrategyModal}
          onHide={toggleStopStrategyModal}
          handleStopClick={handleStopClick}
          subscriptionId={trading_connection?.trading_connection_id}
          strategyName={strategy?.name}
        />
      )}
    </>
  );
};

const StrategyPropTypes = PropTypes.shape({
  strategy_id: PropTypes.string,
  name: PropTypes.string,
  last_7_days: PropTypes.array,
  author: PropTypes.string,
  broker_accounts: PropTypes.array,
  invested_amount: PropTypes.string,
});

const BrokerPropTypes = PropTypes.shape({
  is_stale: PropTypes.bool,
  account_nickname: PropTypes.string,
  account_value: PropTypes.object,
  total_cash: PropTypes.object,
  icon: PropTypes.string,
  existing_allocation: PropTypes.object,
  trading_connection: PropTypes.object,
  broker_account_id: PropTypes.string,
  max_allocation: PropTypes.object,
});

PortfolioBox.propTypes = {
  errorMessage: PropTypes.string,
  strategy: StrategyPropTypes,
  broker: BrokerPropTypes,
  handleStoppedStrategy: PropTypes.func,
  handleAllocationChange: PropTypes.func,
};
